import React, { useEffect, useState } from 'react';
import { MdCreditCard, MdCheckCircle, MdLock } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import API from '../api';
import { useLanguage } from '../i18n';

const fmt = n => Number(n).toLocaleString('en-US');

const STATE_STYLE = {
    trialing: { bg: '#dbeafe', color: '#1d4ed8', label: 'billing.statusTrialing' },
    active: { bg: '#dcfce7', color: '#15803d', label: 'billing.statusActive' },
    grace: { bg: '#fef3c7', color: '#b45309', label: 'billing.statusGrace' },
    read_only: { bg: '#fee2e2', color: '#b91c1c', label: 'billing.statusReadOnly' },
};

const PAY_STYLE = {
    successful: { bg: '#dcfce7', color: '#15803d', label: 'billing.paid' },
    pending: { bg: '#fef3c7', color: '#b45309', label: 'billing.pending' },
    failed: { bg: '#fee2e2', color: '#b91c1c', label: 'billing.failed' },
};

export default function Billing() {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [phone, setPhone] = useState('');
    const [status, setStatus] = useState(null);
    const [catalog, setCatalog] = useState(null);
    const [history, setHistory] = useState([]);
    const [billingInterval, setBillingInterval] = useState('monthly');
    const [busy, setBusy] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        API.get('/billing/status').then(({ data }) => setStatus(data)).catch(() => setStatus({ state: 'complimentary' }));
        API.get('/billing/plans').then(({ data }) => setCatalog(data)).catch(() => setCatalog(null));
        API.get('/billing/history').then(({ data }) => setHistory(data)).catch(() => setHistory([]));
    }, []);

    const phoneOk = /^(\+?250|0)7[2-9]\d{7}$/.test(phone.replace(/[\s-]/g, ''));

    const pay = async planCode => {
        if (!phoneOk) { setError(t('billing.phoneInvalid')); return; }
        setBusy(planCode); setError('');
        try {
            const { data } = await API.post('/billing/checkout', { plan_code: planCode, interval: billingInterval, phone });
            navigate(`/billing/result?tx_ref=${encodeURIComponent(data.tx_ref)}`);
        } catch (err) {
            setError(err.response?.data?.error || t('billing.checkoutFailed'));
            setBusy('');
        }
    };

    if (!status || !catalog) return <div className="text-muted p-3">…</div>;

    if (status.state === 'complimentary') {
        return (
            <div>
                <h4 style={{ fontWeight: 700 }}>{t('billing.title')}</h4>
                <div className="page-card" style={{ padding: 18 }}>{t('billing.noneNeeded')}</div>
            </div>
        );
    }

    const style = STATE_STYLE[status.state] || STATE_STYLE.active;
    const dateText = status.ends_at ? new Date(status.ends_at).toLocaleDateString() : '';
    const message = {
        trialing: t('billing.trialLeft', '', { days: status.days_left }),
        active: t('billing.activeUntil', '', { date: dateText }),
        grace: t('billing.graceLeft', '', { days: status.grace_days_left }),
        read_only: t('billing.readOnly'),
    }[status.state];
    const yearlySaves = catalog.plans.some(p => p.yearly_rwf < p.monthly_rwf * 12);

    return (
        <div>
            <div style={{ marginBottom: 20 }}>
                <h4 style={{ margin: 0, fontWeight: 700 }}>{t('billing.title')}</h4>
                <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>{t('billing.subtitle')}</p>
            </div>

            <div className="page-card" style={{ padding: 18, marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
                    <MdCreditCard style={{ color: '#0b5ed7' }} />
                    <strong>{status.plan_name || t('billing.plan')}</strong>
                    <span style={{ background: style.bg, color: style.color, fontWeight: 700, fontSize: 12, padding: '2px 10px', borderRadius: 999 }}>
                        {t(style.label)}
                    </span>
                </div>
                <div style={{ color: '#475569', fontSize: 14 }}>{message}</div>
            </div>

            {error && <div className="alert alert-danger" style={{ fontSize: 13 }}>{error}</div>}
            {!catalog.payments_enabled && <div className="alert alert-warning" style={{ fontSize: 13 }}>{t('billing.paymentsOff')}</div>}

            <div className="page-card" style={{ padding: 18, marginBottom: 20 }}>
                <label className="form-label" style={{ fontWeight: 600, fontSize: 14 }}>{t('billing.phoneLabel')}</label>
                <input type="tel" inputMode="tel" className="form-control" style={{ maxWidth: 280 }} value={phone}
                    placeholder={t('billing.phonePlaceholder')} onChange={e => setPhone(e.target.value)} />
                <div style={{ color: '#64748b', fontSize: 12.5, marginTop: 6 }}>{t('billing.phoneHelp')}</div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
                <h6 style={{ margin: 0, fontWeight: 700 }}>{t('billing.choosePlan')}</h6>
                <div className="btn-group" role="group">
                    {['monthly', 'yearly'].map(option => (
                        <button key={option} type="button"
                            className={`btn btn-sm ${billingInterval === option ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setBillingInterval(option)}>
                            {t(option === 'monthly' ? 'billing.monthly' : 'billing.yearly')}
                            {option === 'yearly' && yearlySaves ? ` · ${t('billing.yearlyHint')}` : ''}
                        </button>
                    ))}
                </div>
            </div>

            <div className="row g-3 mb-4">
                {catalog.plans.map(plan => {
                    const price = billingInterval === 'monthly' ? plan.monthly_rwf : plan.yearly_rwf;
                    const current = status.plan_code === plan.code && status.state === 'active';
                    return (
                        <div className="col-12 col-md-4" key={plan.code}>
                            <div className="page-card" style={{ padding: 20, height: '100%', border: current ? '2px solid #0b5ed7' : undefined }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <h5 style={{ margin: 0, fontWeight: 700 }}>{plan.name}</h5>
                                    {current && <span style={{ fontSize: 11, fontWeight: 700, color: '#0b5ed7' }}><MdCheckCircle /> {t('billing.currentBadge')}</span>}
                                </div>
                                <div style={{ margin: '14px 0 4px' }}>
                                    <span style={{ fontSize: 28, fontWeight: 800 }}>{fmt(price)}</span>{' '}
                                    <span style={{ color: '#64748b', fontSize: 13 }}>{catalog.currency} {t(billingInterval === 'monthly' ? 'billing.perMonth' : 'billing.perYear')}</span>
                                </div>
                                <div style={{ color: '#475569', fontSize: 13, marginBottom: 12 }}>
                                    {plan.max_users ? t('billing.users', '', { n: plan.max_users }) : t('billing.unlimitedUsers')}
                                </div>
                                <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>{t('billing.includes')}</div>
                                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px', fontSize: 13 }}>
                                    {(catalog.features || []).map(feature => {
                                        const included = (plan.features || []).includes(feature.key);
                                        return (
                                            <li key={feature.key} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', margin: '3px 0', color: included ? '#0f172a' : '#94a3b8', textDecoration: included ? 'none' : 'line-through' }}>
                                                <span style={{ color: included ? '#15803d' : '#94a3b8' }}>{included ? '✓' : '✕'}</span>
                                                <span>{t(`billing.feature.${feature.key}`, feature.label)}</span>
                                            </li>
                                        );
                                    })}
                                </ul>
                                <button className="btn btn-primary w-100" disabled={!catalog.payments_enabled || !!busy || !phoneOk}
                                    onClick={() => pay(plan.code)}>
                                    {busy === plan.code ? t('billing.redirecting') : t('billing.payButton')}
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            <p style={{ color: '#64748b', fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 6 }}>
                <MdLock /> {t('billing.secureNote')}
            </p>

            <h6 style={{ fontWeight: 700, marginTop: 24 }}>{t('billing.history')}</h6>
            <div className="page-card" style={{ padding: 0, overflowX: 'auto' }}>
                {history.length === 0 ? (
                    <div style={{ padding: 16, color: '#64748b', fontSize: 13 }}>{t('billing.noPayments')}</div>
                ) : (
                    <table className="table mb-0" style={{ fontSize: 13 }}>
                        <thead>
                            <tr>
                                <th>{t('billing.colDate')}</th><th>{t('billing.colPlan')}</th>
                                <th>{t('billing.colAmount')}</th><th>{t('billing.colStatus')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.map(row => {
                                const pay = PAY_STYLE[row.status] || PAY_STYLE.pending;
                                return (
                                    <tr key={row.tx_ref}>
                                        <td>{new Date(row.paid_at || row.created_at).toLocaleDateString()}</td>
                                        <td style={{ textTransform: 'capitalize' }}>{row.plan_code} · {t(row.billing_interval === 'monthly' ? 'billing.monthly' : 'billing.yearly')}</td>
                                        <td>{fmt(row.amount)} {row.currency}</td>
                                        <td><span style={{ background: pay.bg, color: pay.color, fontWeight: 700, fontSize: 11.5, padding: '2px 8px', borderRadius: 999 }}>{t(pay.label)}</span></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
